import { isInFuturesApp } from '../../base/env/app/isInFuturesApp';
import { isInIFindApp } from '../../base/env/app/isInIFindApp';
import { isInThsApp } from '../../base/env/app/isInThsApp';
import { isGPhone } from '../../base/env/platform/isGPhone';
import { isHarmony } from '../../base/env/platform/isHarmony';
import { isIPhone } from '../../base/env/platform/isIPhone';
import { isHigherInnerVersion } from '../../base/version/isHigherInnerVersion';
import { buildGetHQRequestParams } from './request';
import type { CodeWithMarket, HqRequestParams } from './types';

type HqDataHandler = (rawData: Record<string, string[]>) => void;
type HqErrorHandler = (error: unknown) => void;

const hqHandlerMap = new Map<string, HqDataHandler>();
const hqErrorHandlerMap = new Map<string, HqErrorHandler>();
const requestParamsMap = new Map<string, HqRequestParams>();
let globalHandlerRegistered = false;

// ---- getHQ 协议 (旧版期货通 iOS) ----
const getHQHandlerMap = new Map<string, HqDataHandler>();
const getHQErrorHandlerMap = new Map<string, HqErrorHandler>();
let getHQHandlerRegistered = false;

let _useGetHQ: boolean | null = null;

function shouldUseGetHQ(): boolean {
  if (_useGetHQ !== null) return _useGetHQ;
  if (!isInFuturesApp() || !isIPhone()) {
    _useGetHQ = false;
    return false;
  }
  _useGetHQ = !isHigherInnerVersion({ iosVersion: '037.08.389' });
  return _useGetHQ;
}

function filterGetHQData(
  raw: Record<string, string[]>,
  stocks: CodeWithMarket[],
): Record<string, string[]> | null {
  const codes = raw['4'] || [];
  const markets = raw['34338'] || [];
  const indices: number[] = [];
  for (let i = 0; i < codes.length; i++) {
    if (stocks.includes(`${codes[i]}_${markets[i]}`)) {
      indices.push(i);
    }
  }
  if (indices.length === 0) return null;

  const filtered: Record<string, string[]> = {};
  Object.keys(raw).forEach((key) => {
    filtered[key] = indices.map((i) => raw[key][i]);
  });
  return filtered;
}

function registerGetHQHandler() {
  if (getHQHandlerRegistered) return;

  window.registerWebHandler?.('getHQ', (data: any) => {
    if (data?.method === 'normal' && data?.data) {
      getHQHandlerMap.forEach((handler, onlineId) => {
        try {
          const stocks = requestParamsMap.get(onlineId)?._stocks || [];
          if (stocks.length === 0) return;
          const filtered = filterGetHQData(data.data, stocks);
          if (filtered) handler(filtered);
        } catch (e) {
          getHQErrorHandlerMap.get(onlineId)?.(e);
        }
      });
    } else {
      getHQErrorHandlerMap.forEach((handler) => {
        try { handler(data); } catch { /* 静默忽略 error handler 自身异常 */ }
      });
    }
  });
  getHQHandlerRegistered = true;
}

/** 合并所有活跃实例的 stocks 并发给 native，或全部清空时断开 */
function syncGetHQStocks() {
  if (getHQHandlerMap.size === 0) {
    window.callNativeHandler?.('getHQ', { method: 'disconnect' });
    return;
  }

  const allStocks: CodeWithMarket[] = [];
  const allColumOrder: string[] = [];
  const seenStocks = new Set<string>();
  const seenCols = new Set<string>();
  getHQHandlerMap.forEach((_, id) => {
    requestParamsMap.get(id)?._stocks?.forEach((s) => {
      if (!seenStocks.has(s)) { seenStocks.add(s); allStocks.push(s); }
    });
    requestParamsMap.get(id)?._columOrder?.forEach((c) => {
      if (!seenCols.has(c)) { seenCols.add(c); allColumOrder.push(c); }
    });
  });

  window.callNativeHandler?.('getHQ', buildGetHQRequestParams(allStocks, allColumOrder));
}

function registerGlobalHandler() {
  if (globalHandlerRegistered) return;

  const handler = (data: any) => {
    const onlineId = data?.head?.onlineId;
    if (!onlineId) return;

    const errorCode = data?.head?.errorCode;
    if (errorCode && errorCode !== '0') {
      hqErrorHandlerMap.get(onlineId)?.(data);
      return;
    }

    const dataHandler = hqHandlerMap.get(onlineId);
    if (!dataHandler || !data?.body?.dataDict) return;

    try {
      const parsed =
        typeof data.body.dataDict === 'string'
          ? JSON.parse(data.body.dataDict)
          : data.body.dataDict;
      dataHandler(parsed);
    } catch (error) {
      hqErrorHandlerMap.get(onlineId)?.(error);
    }
  };

  if (window.registerWebHandler) {
    window.registerWebHandler('UnifiedRequestBridge', handler);
    globalHandlerRegistered = true;
  } else {
    window.registerWebListener?.('UnifiedRequestBridge', handler);
    globalHandlerRegistered = Boolean(window.registerWebListener);
  }
}

function whenAndroidBridgeReady(callback: () => void) {
  if (window.WebViewJavascriptBridge) {
    callback();
    return;
  }

  document.addEventListener('WebViewJavascriptBridgeReady', callback, { once: true });
}

function getBridgeAPI(): 'onlineHandler' | 'nativeHandler' {
  if (isIPhone()) return 'nativeHandler';
  if (isGPhone()) return 'onlineHandler';

  if (isHarmony()) {
    if (isInFuturesApp()) return 'onlineHandler';
    if (isInThsApp() || isInIFindApp()) return 'nativeHandler';
  }

  return 'nativeHandler';
}

export function sendHqRequest(
  params: HqRequestParams,
  handlers: {
    onData: HqDataHandler;
    onError: HqErrorHandler;
  },
) {
  const onlineId = params.onlineId;

  if (shouldUseGetHQ()) {
    registerGetHQHandler();
    getHQHandlerMap.set(onlineId, handlers.onData);
    getHQErrorHandlerMap.set(onlineId, handlers.onError);
    requestParamsMap.set(onlineId, params);
    syncGetHQStocks();
    return;
  }

  registerGlobalHandler();
  if (hqHandlerMap.has(onlineId)) {
    console.warn(`[HqManager] onlineId "${onlineId}" 已被使用，请使用不同的 onlineId 来隔离实例。`);
  }
  hqHandlerMap.set(onlineId, handlers.onData);
  hqErrorHandlerMap.set(onlineId, handlers.onError);
  requestParamsMap.set(onlineId, params);

  if (getBridgeAPI() === 'onlineHandler') {
    whenAndroidBridgeReady(() => {
      if (requestParamsMap.get(onlineId) !== params) return;
      const bridge = window.WebViewJavascriptBridge;
      if (!bridge?.callOnlineHandler) return;
      bridge.callOnlineHandler('UnifiedRequestBridge', onlineId, params);
    });
    return;
  }

  window.callNativeHandler?.('UnifiedRequestBridge', params);
}

export function clearHqRequest(onlineId: string) {
  const originalParams = requestParamsMap.get(onlineId);
  if (!originalParams) return false;

  if (shouldUseGetHQ()) {
    getHQHandlerMap.delete(onlineId);
    getHQErrorHandlerMap.delete(onlineId);
    requestParamsMap.delete(onlineId);
    syncGetHQStocks();
    return true;
  }

  if (getBridgeAPI() === 'onlineHandler') {
    whenAndroidBridgeReady(() => {
      const bridge = window.WebViewJavascriptBridge;
      if (!bridge?.clearOnlineHandler) return;
      bridge.clearOnlineHandler([onlineId]);
    });
  } else {
    window.callNativeHandler?.('UnifiedRequestBridge', {
      ...originalParams,
      actionType: 'unSubscribe',
    });
  }

  requestParamsMap.delete(onlineId);
  hqHandlerMap.delete(onlineId);
  hqErrorHandlerMap.delete(onlineId);
  return true;
}

export function removeHqHandler(onlineId: string) {
  requestParamsMap.delete(onlineId);
  hqHandlerMap.delete(onlineId);
  hqErrorHandlerMap.delete(onlineId);

  if (shouldUseGetHQ()) {
    getHQHandlerMap.delete(onlineId);
    getHQErrorHandlerMap.delete(onlineId);
  }
}